import { Delete, FileUpload } from '@mui/icons-material';
import { Box, Button, TextField, type BoxProps } from '@mui/material';
import { useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { ImageCropperModal } from 'src/shared/components/organisms/imageCropperModal';
import { useEventContext } from 'src/shared/contexts/eventContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteEventImageById } from 'src/shared/services/eventImageService';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { dataUrlToFile } from 'src/shared/functions/dataUrlToFile';

const ImgBox = Box as React.ComponentType<BoxProps<'img'>>;

export const EventImageUpload = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { control } = useFormContext();
  const { event, setEvent, uploadedImageUrl, uploadedImageFile, croppedImage, setUploadedImageUrl, setUploadedImageFile, setCroppedImage, editMode } =
    useEventContext();

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [openConfirmDelete, setConfirmDeleteOpen] = useState<boolean>(false);

  const hasImages = event && event?.images?.length > 0;

  // Raderar lokal bild i context i CreateModal och i EditModal om person
  // ångrar sig om och om igen
  const onDeleteHandlerAgainstContext = () => {
    setUploadedImageUrl('');
    setUploadedImageFile(undefined);
    setCroppedImage(undefined);
  };

  // Körs i EditModal, tar bort bild från databas och i eventet i context
  // för att efter det hantera nytillagd bild lokalt.
  const onDeleteHandlerEditMode = useCallback(async () => {
    try {
      await deleteEventImageById(event?.images[0].id);
      if (isMounted()) {
        toast.success(<>{t('event:imageDeleted')}</>);
        if (event) {
          setEvent({ ...event, images: [] });
        }
      }
    } catch (error) {
      if (isMounted()) ConsoleHelper.log('error', error);
    }
  }, [event, isMounted, setEvent, t]);

  const handleChange = (e) => {
    setUploadedImageUrl(URL.createObjectURL(e.target.files[0]));
    setUploadedImageFile(e.target.files[0]);
    setCropDialogOpen(true);

    if (hasImages) {
      onDeleteHandlerEditMode();
    }
  };

  return (
    <>
      <FormFieldset legend={t('event:eventImageHeader')} fullHeight={!hasImages} childrenTop={uploadedImageUrl.length > 0}>
        <Box sx={{ pt: 1, px: 0.5, pb: 0 }}>
          {editMode && event && (
            <>
              {!hasImages && !uploadedImageFile && (
                <Button variant='outlined' component='label' size='small' sx={{ mb: 1 }} endIcon={<FileUpload fontSize='small' />}>
                  {t('event:chooseImage')}
                  <Box sx={{ display: 'none' }}>
                    <Controller
                      name='files'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          hidden
                          type='file'
                          id='fileInput'
                          inputProps={{
                            hidden: true,
                            accept: 'image/*',
                          }}
                          onChange={(e) => handleChange(e)}
                          sx={{}}
                        />
                      )}
                    />
                  </Box>
                </Button>
              )}
              {hasImages && (
                <>
                  <ImgBox
                    component='img'
                    sx={{ height: 'auto', width: '100%', borderRadius: 1 }}
                    src={`data:image/png;base64,${event?.images?.[0].base64}`}
                    alt={t('event:eventImageAltText')}
                  />
                  <Button variant='outlined' size='small' endIcon={<Delete />} onClick={() => setConfirmDeleteOpen(true)}>
                    {t('common:button.remove')}
                  </Button>
                </>
              )}
              {uploadedImageUrl && uploadedImageFile && (
                <>
                  <ImgBox
                    component='img'
                    src={uploadedImageUrl}
                    alt={t('event:eventImageAltText')}
                    sx={{ width: '100%', maxHeight: '100%', objectFit: 'cover', objectPosition: 'bottom', m: 0, p: 0 }}
                  />
                  <Button variant='outlined' size='small' endIcon={<Delete />} onClick={onDeleteHandlerAgainstContext}>
                    {t('common:button.remove')}
                  </Button>
                </>
              )}
            </>
          )}
          {!editMode && (
            <>
              <Button variant='outlined' component='label' size='small' sx={{ mb: 1 }} endIcon={<FileUpload fontSize='small' />}>
                {t('event:chooseImage')}
                <Box sx={{ display: 'none' }}>
                  <Controller
                    name='files'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        hidden
                        type='file'
                        id='fileInput'
                        inputProps={{
                          hidden: true,
                          accept: 'image/*',
                        }}
                        onChange={(e) => handleChange(e)}
                        sx={{}}
                      />
                    )}
                  />
                </Box>
              </Button>

              {uploadedImageUrl && croppedImage && (
                <ImgBox
                  component='img'
                  src={croppedImage as any}
                  alt={t('event:eventImageAltText')}
                  sx={{ width: '100%', maxHeight: '100%', objectFit: 'cover', objectPosition: 'bottom', m: 0, p: 0 }}
                />
              )}
              {uploadedImageFile && (
                <Button variant='outlined' size='small' endIcon={<Delete />} onClick={onDeleteHandlerAgainstContext}>
                  {t('common:button.remove')}
                </Button>
              )}
            </>
          )}
        </Box>
      </FormFieldset>
      <ImageCropperModal
        open={cropDialogOpen}
        image={uploadedImageUrl}
        onComplete={(imagePromise) => {
          ConsoleHelper.log('onComplete', imagePromise);
          imagePromise.then((image) => {
            setUploadedImageFile(dataUrlToFile(image, 'eventbild.jpg'));
            setUploadedImageUrl(image);
            setCroppedImage(image);
            setCropDialogOpen(false);
          });
        }}
        onAbort={() => {
          onDeleteHandlerAgainstContext();
          setCropDialogOpen(false);
        }}
        containerStyle={{
          position: 'relative',
          width: '100%',
          height: 300,
          background: '#333',
        }}
      />
      <GenericDialog
        maxWidth='xs'
        open={openConfirmDelete}
        showDividers={false}
        saveLabel={t('common:button.yes')}
        abortLabel={t('common:button.no')}
        dialogTitle={t('event:confirm')}
        dialogContent={t('event:wantToRemoveImage')}
        onClose={() => setConfirmDeleteOpen(false)}
        onSave={() => {
          setConfirmDeleteOpen(false);
          onDeleteHandlerEditMode();
        }}
      />
    </>
  );
};
